import { BellIcon } from '@heroicons/react/24/outline';
import { BellIcon as SolidBell } from '@heroicons/react/24/solid';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useId } from 'react';
import { UserContextV2 } from '~/app';
import { HeaderBrandMenu } from '~/components/BrandSwitcher';
import { NotificationBadge } from '~/components/NotificationBadge';
import { McvLogo } from '~/components/logos';
import { TutorialVideoButton } from '~/features/tutorials/TutorialVideoButton';
import { PROVIDER_ROLE_CODE } from '~/features/user/constants';
import { useNotificationsRef } from '~/features/user/userSelectors';
import { paths } from '~/routing';
import { HeaderHelpMenu } from '../HeaderHelpMenu/HeaderHelpMenu';

export const NavHeader = () => {
  const { isOpen, toggleNotificationsOpen, notificationsRef, notifications } =
    useNotificationsRef();
  // Above the `!vaultRef` early return — hook order must stay stable.
  const badgeId = useId();

  const userRef = UserContextV2.useActorRef();
  const { vaultRef, roleCode } = useSelector(userRef, (state) => state.context);

  const isDelegating = useSelector(userRef, (state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );

  if (!vaultRef) return null;

  const setActivePage = (page: string) => vaultRef.send({ type: 'ON_SET_PAGE', payload: { page } });

  return (
    <nav
      aria-label="Vault header"
      className="h-[72px] w-full bg-white border-b border-grey-900 flex items-center justify-between px-6"
    >
      {/* Left: Logo — also the MCV-1513 brand switcher trigger */}
      <div className="max-[1360px]:hidden flex items-center">
        <HeaderBrandMenu
          brand="mcv"
          allowBrandSwitch={!isDelegating}
          surface="nav-header"
          primaryPath={paths.vault.dashboard}
          primaryLabel="Go to dashboard"
          onPrimaryClick={() => setActivePage('dashboard')}
          triggerAriaLabel="MedCredVault menu"
          switchTargetPath={
            roleCode === PROVIDER_ROLE_CODE ? paths.hlx.jobOpportunities : undefined
          }
        >
          <McvLogo label="side" height={40} />
        </HeaderBrandMenu>
      </div>

      {/* Spacer: keeps the logo left and the ?/bell right; it must stay mounted at every
          width (the logo above is hidden below 1360px). */}
      <div className="flex-1" />

      {/* Right: the MCV tour video (providers only), the help (?) menu, then the bell
          [MCV-1724] [MCV-1780] [MCV-1945] */}
      <div className="flex items-center gap-3 pr-2">
        {roleCode === PROVIDER_ROLE_CODE && (
          <TutorialVideoButton videoId="mcvTour" tooltipAlign="end" />
        )}
        <HeaderHelpMenu brand="mcv" canContactSupport={!isDelegating} />
        {notificationsRef && (
          <button
            onClick={toggleNotificationsOpen}
            className="relative p-1.5 hover:bg-grey-900/50 rounded-control transition-colors hover:cursor-pointer"
            aria-label="Toggle notifications"
            data-notification-toggle="true"
            aria-describedby={notifications.length > 0 ? badgeId : undefined}
          >
            {isOpen ? (
              <SolidBell className="size-5 text-grey-400" data-cy={CT.ToolBar.bellIcon} />
            ) : (
              <BellIcon className="size-5 text-grey-400" data-cy={CT.ToolBar.bellIcon} />
            )}
            <NotificationBadge count={notifications.length} id={badgeId} />
          </button>
        )}
      </div>
    </nav>
  );
};
